import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { Branch, DeleteBranchSettings } from '@/client/branches';
import { FilterNamesEnum, getFiltersFromColumns } from '@/client/helpers';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import {
  RedirectPaths,
  RedirectPathsEnum,
  TableNamesEnum,
} from '@/common/constants';
import { BRANCHES_GUIDE } from '@/common/constants/external-urls';
import { TranslationFunctionType } from '@/common/types';
import { DeleteBranchModal } from '@/components/branches/forms/modals/DeleteBranchModal';
import {
  DataTable,
  DataTableActions,
  DataTableColumnsMultiselect,
  DataTableColumnType,
  DataTableFilters,
  DataTableToolbar,
  FilterTypeEnum,
} from '@/components/tables/crud';
import { useBranches, useDeleteBranches, useUpdateBranch } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import BranchesBlankPageImg from '@/images/blank-pages/branches-blank-page.svg';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { CustomSwitch } from '@/system-settings';
import { BlankPage } from '@/ui/blank-page/BlankPage';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import { isDemoRestrictedMode } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputSwitchChangeEvent } from 'primereact/inputswitch';
import { MenuItem } from 'primereact/menuitem';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledFlexContainer = styled(FlexContainer)`
  margin-top: var(--default-padding);

  div {
    display: flex;
  }
`;

const StyledParagraph = styled.p`
  font-size: var(--xsmall-font-size);
  margin: 0;
`;

const ToggleBranchModal: React.FC<{
  visible: boolean;
  branch: Branch | null;
  isLoading: boolean;
  onConfirm: (keepUsersActive: boolean) => void;
  onHide: () => void;
}> = ({ visible, onHide, branch, onConfirm, isLoading }) => {
  const { t } = useTranslation();
  const [keepUsersActive, setKeepUsersActive] = useState(false);

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={`${
        branch?.active ? t('generic.deactivate') : t('generic.activate')
      } ${t('dialog.confirm')}`}
      modal
      draggable={false}
      style={{ width: '550px' }}
    >
      <div className="mb-4">
        <span>
          {branch?.active ? t('branch.deactivate') : t('branch.activate')}
        </span>
        {branch?.active && (
          <StyledFlexContainer direction="column" align="flex-start">
            <CustomSwitch
              className="mb-2"
              label={t('branch.keepUsersActive')}
              onChange={(e: InputSwitchChangeEvent) => {
                setKeepUsersActive(e.value);
              }}
              checked={keepUsersActive}
            />

            {!keepUsersActive && (
              <Message
                severity="warn"
                style={{ maxWidth: '550px' }}
                text={() => (
                  <>
                    <strong className="font-semibold">
                      {t('branch.deactivate.users.warning.heading')}
                    </strong>
                    <StyledParagraph>
                      {branch?.usersCount === 1
                        ? t('branch.deactivate.users.warning.info.singular')
                        : t('branch.deactivate.users.warning.info.plural', {
                            count: branch?.activeUsersCount,
                          })}
                    </StyledParagraph>
                  </>
                )}
                icon="pi pi-info-circle"
                className="mt-2"
              />
            )}
          </StyledFlexContainer>
        )}
      </div>
      <FlexContainer justify="flex-end">
        <AppButton
          label={t('dialog.yes')}
          icon="pi pi-check"
          severity="secondary"
          type="text"
          onClick={() => onConfirm(keepUsersActive)}
          isDisabled={isLoading}
        />
        <AppButton
          label={t('dialog.no')}
          icon="pi pi-times"
          severity="secondary"
          onClick={onHide}
        />
      </FlexContainer>
    </Dialog>
  );
};

const getBreadcrumbs = (
  account: Account,
  t: TranslationFunctionType,
): MenuItem[] => {
  return [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('branches'),
      url: RedirectPaths[RedirectPathsEnum.BRANCHES](),
      template: AppBreadCrumbTemplate,
    },
  ];
};

export const BranchesPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = usePermission();
  const { skip, take, sort, apiFilters, onSort, onFilter, onPage } = useTable({
    tableName: TableNamesEnum.BRANCHES_LIST,
  });

  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(account, currentUser);

  const [showToggleModal, setShowToggleModal] = useState(false);
  const [branchToToggle, setBranchToToggle] = useState<Branch | null>(null);
  const [isToggleLoading, setIsToggleLoading] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBranchForDelete, setSelectedBranchForDelete] =
    useState<Branch | null>(null);

  const { isLoading: isInitialLoading, branches: initialBranches } =
    useBranches({
      accountId: account?.id,
      take: 1,
      skip: 0,
    });

  const { isLoading, branches, refetch } = useBranches({
    accountId: account?.id,
    take,
    skip,
    filters: apiFilters,
    sort:
      sort && sort.length > 0 && sort[0] !== 'created'
        ? [sort.join(',')]
        : ['left,asc'],
    enabled: !!initialBranches?.count,
  });

  const updateBranch = useUpdateBranch();
  const deleteBranches = useDeleteBranches();

  const navigate = useNavigate();
  const location = useLocation();

  const columns: DataTableColumnType[] = [
    {
      field: 'name',
      header: t('generic.name'),
      sortable: true,
      filterable: true,
      filters: { field: FilterNamesEnum.NAME, type: FilterTypeEnum.TEXT },
      render: (row: Branch) => {
        const branchNamesArray: string[] = row?.tree
          ? row.tree.split(' / ')
          : [];
        branchNamesArray.pop();

        return (
          <>
            {!!branchNamesArray.length && (
              <div className="secondary-text">
                {`${branchNamesArray.join(' / ')} /`}
              </div>
            )}
            <span>{row.name}</span>
          </>
        );
      },
    },
    {
      field: 'activeUsersCount',
      header: t('branches.active.users'),
      sortable: true,
      filterable: false,
      render: (row: Branch) => row?.activeUsersCount || 0,
    },
    {
      field: 'inactiveUsersCount',
      header: t('branches.inactive.users'),
      sortable: true,
      filterable: false,
      render: (row: Branch) => row?.inactiveUsersCount || 0,
    },
    {
      field: 'updated',
      header: t('generic.updated'),
      sortable: true,
      filterable: false,
      render: (row: Branch) =>
        row?.updated ? <FormatDate date={row?.updated} /> : t('generic.never'),
    },
    {
      field: 'active',
      header: t('generic.active'),
      sortable: true,
      filterable: true,
      filters: { type: FilterTypeEnum.SELECT_ACTIVE_INACTIVE },
      render: (row: Branch) =>
        row.active ? t('generic.active') : t('generic.inactive'),
    },
    {
      field: 'actions',
      header: t('generic.actions'),
      sortable: false,
      filterable: false,
      style: {
        width: '80px',
        textAlign: 'center' as const,
      },
      render: (row: Branch) => {
        if (deletingBranchId === row.id) {
          return (
            <FlexContainer>
              <ProgressSpinner style={{ width: '24px', height: '24px' }} />
            </FlexContainer>
          );
        }
        return (
          <DataTableActions
            disabled={menuItems(row).length < 1}
            menuItems={menuItems(row)}
          />
        );
      },
    },
  ];

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const defaultVisibleColumns = columns
    .filter((column) => !['inactiveUsersCount'].includes(column.field))
    .map((column) => column.field);
  const alwaysVisibleColumns = ['name', 'actions'];
  //

  const menuItems = (branch: Branch) => {
    const menu: MenuItem[] = [
      {
        label: t('users'),
        icon: 'pi pi-users',
        command: () =>
          navigate(RedirectPaths[RedirectPathsEnum.BRANCHES_USERS](branch.id)),
      },
    ];

    if (
      can(Actions.UPDATE, Subjects.BRANCHES) &&
      (!account?.isSystem || currentUser?.role.code === SystemRoles.DEVELOPER)
    ) {
      menu.push(
        ...[
          {
            items: [
              {
                label: t('generic.edit'),
                icon: 'pi pi-pencil',
                command: () => {
                  navigate(
                    RedirectPaths[RedirectPathsEnum.BRANCHES_EDIT](branch.id),
                    { state: { from: location.pathname + location.search } },
                  );
                },
              },
              {
                label: branch.active
                  ? t('generic.deactivate')
                  : t('generic.activate'),
                icon: branch.active ? 'pi pi-minus-circle' : 'pi pi-check',
                command: isDemoAccount
                  ? undefined
                  : () => {
                      setBranchToToggle(branch);
                      setShowToggleModal(true);
                    },
              },
              {
                label: t('generic.delete'),
                icon: 'pi pi-times',
                command: isDemoAccount
                  ? undefined
                  : () => openDeleteModal(branch),
              },
            ],
          },
        ],
      );
    }

    return menu;
  };

  const handleToggleItemsStatus = async (
    branchId: string,
    keepUsersActive: boolean,
    active?: boolean,
  ) => {
    if (isDemoAccount) return;
    setIsToggleLoading(true);
    try {
      await updateBranch.update({
        accountId: (account as Account)?.id,
        branchId: branchId,
        updates: {
          active: !active,
          deActivateOptions: {
            keepUsersActive: keepUsersActive,
          },
        },
      });
      await refetch();
      toast?.success(
        t('toast.success'),
        active ? t('branch.deactivated') : t('branch.activated'),
      );
      setShowToggleModal(false);
      setBranchToToggle(null);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      setIsToggleLoading(false);
    }
  };

  const [deletingBranchId, setDeletingBranchId] = useState<string | null>(null);

  const handleDeleteBranchWithModal = async (
    settings: DeleteBranchSettings,
  ) => {
    if (isDemoAccount || !selectedBranchForDelete) return;

    setDeletingBranchId(selectedBranchForDelete?.id);
    try {
      await deleteBranches.delete({
        accountId: (account as Account)?.id,
        branchId: selectedBranchForDelete?.id,
        shouldDeleteUsers: settings.shouldDeleteUsers,
        reAssignUsersToBranch: settings.reAssignUsersToBranch,
        reAssignChildBranchesTo: settings.reAssignChildBranchesTo,
      });
      await refetch();
      toast?.success(t('toast.success'), t('branch.deleted'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      setShowDeleteModal(false);
      setSelectedBranchForDelete(null);
      setDeletingBranchId(null);
    }
  };

  const openDeleteModal = (branch: Branch) => {
    setSelectedBranchForDelete(branch);
    setShowDeleteModal(true);
  };

  const toolbar = (
    <DataTableToolbar>
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        <DataTableFilters
          filters={getFiltersFromColumns(columns)}
          onFilter={onFilter}
          tableName={TableNamesEnum.BRANCHES_LIST}
        />
        {can(Actions.UPDATE, Subjects.BRANCHES) &&
          (!account?.isSystem ||
            currentUser?.role.code === SystemRoles.DEVELOPER) && (
            <AppButton
              label={t('button.createNew')}
              severity="secondary"
              onClick={
                isDemoAccount
                  ? undefined
                  : () => {
                      navigate(
                        RedirectPaths[RedirectPathsEnum.BRANCHES_CREATE](),
                        {
                          state: { from: location.pathname + location.search },
                        },
                      );
                    }
              }
            />
          )}
      </FlexContainer>
      <DataTableColumnsMultiselect
        columns={columns}
        tableName={TableNamesEnum.BRANCHES_LIST}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        alwaysVisibleColumns={alwaysVisibleColumns}
      />
    </DataTableToolbar>
  );

  const onRowClick = (e: DataTableRowClickEvent) => {
    if (isLoading) return;
    navigate(RedirectPaths[RedirectPathsEnum.BRANCHES_EDIT](e.data.id), {
      state: { from: location.pathname + location.search },
    });
  };

  return (
    <FlexContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      style={{ minHeight: '100%' }}
    >
      <AppBreadCrumb model={getBreadcrumbs(account as Account, t)} />
      <h1>{t('branches')}</h1>
      {!initialBranches?.count ? (
        <BlankPage
          isLoading={isInitialLoading}
          image={BranchesBlankPageImg}
          title={t('branches.blank.heading')}
          description={t('branches.blank.description')}
          mainCtaText={t('branches.blank.cta.create')}
          mainCtaOnClick={() =>
            navigate(RedirectPaths[RedirectPathsEnum.BRANCHES_CREATE]())
          }
          secondaryCtaText={t('branches.blank.cta.readMore')}
          secondaryCtaLink={BRANCHES_GUIDE}
          additionsText={[
            t('branches.blank.additions.1'),
            t('branches.blank.additions.2'),
            t('branches.blank.additions.3'),
          ]}
        />
      ) : (
        <DataTable
          data={branches?.result}
          count={branches?.count}
          isLoading={isLoading}
          toolbar={toolbar}
          columns={columns}
          visibleColumns={visibleColumns}
          rows={take}
          skip={skip}
          onPage={onPage}
          onSort={onSort}
          sort={sort}
          onRowClick={onRowClick}
        />
      )}

      {selectedBranchForDelete && (
        <DeleteBranchModal
          visible={showDeleteModal}
          branchName={selectedBranchForDelete.name}
          branchId={selectedBranchForDelete.id}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedBranchForDelete(null);
          }}
          onDelete={handleDeleteBranchWithModal}
        />
      )}

      <ToggleBranchModal
        visible={showToggleModal}
        branch={branchToToggle}
        isLoading={isToggleLoading}
        onConfirm={(keepUsersActive) =>
          handleToggleItemsStatus(
            branchToToggle?.id || '',
            keepUsersActive,
            branchToToggle?.active,
          )
        }
        onHide={() => {
          setShowToggleModal(false);
          setBranchToToggle(null);
        }}
      />
    </FlexContainer>
  );
};
